import { DialogDisclosure, Heading, HeadingLevel } from "@ariakit/react";
import { DialogTrigger } from "@indietabletop/appkit";
import { button } from "../../common/common.css.ts";
import { database } from "../../database.ts";
import { EditMode } from "../../engine/EditMode.ts";
import { FailureView } from "../failures/FailureView.tsx";
import { ChooseModsDialog } from "./ChooseModsDialog.tsx";
import { ChooseRepairsDialog } from "./ChooseRepairsDialog.tsx";
import { ChooseVehicleWeaponsDialog } from "./ChooseWeaponsDialog.tsx";
import { WriteOffView } from "./InMemoriam.tsx";
import { IssuesList } from "./IssueCard.tsx";
import { ItemNotFound } from "./ItemNotFound.tsx";
import { LoadoutItem } from "./LoadoutItem.tsx";
import { ModelContext, useVehicleContext } from "./ModelContext.tsx";
import { ModelCostCap } from "./ModelCostCap.tsx";
import { MysteriousRedButton } from "./MysteriousRedButton.tsx";
import { ProfileInfo } from "./ProfileInfo.tsx";
import { common, gang } from "./style.css.ts";
import { useVehicle } from "./useVehicle.tsx";
import { WeaponsSection } from "./WeaponsSection.tsx";

function VehicleProfileInfo() {
  const vehicle = useVehicleContext();
  return (
    <ProfileInfo
      name={vehicle.name}
      type={
        vehicle.type.id === vehicle.originalType.id
          ? vehicle.type.name
          : `${vehicle.type.name} (${vehicle.originalType.name})`
      }
      cap={<ModelCostCap model={vehicle} />}
      stats={[
        { label: "Engine", value: vehicle.engine },
        { label: "Seats", value: vehicle.seats },
        { label: "Toughness", value: vehicle.toughness },
      ]}
    />
  );
}

function VehicleWeaponsSection() {
  const vehicle = useVehicleContext();
  const { weapons, weaponSpecialRules } = vehicle;

  if (weapons.length === 0) {
    return null;
  }

  return (
    <WeaponsSection
      weapons={weapons}
      weaponSpecialRules={weaponSpecialRules}
      onRemoveWeapon={(weapon) => {
        const nextVehicle = vehicle.removeWeapon(weapon.index);
        void database.gangs.updateOne(nextVehicle.gang.data);
      }}
    />
  );
}

function VehicleActions() {
  const vehicle = useVehicleContext();

  return (
    <div className={common.actions}>
      {vehicle.can(EditMode.ADD, "weapons") && (
        <DialogTrigger>
          <ChooseVehicleWeaponsDialog />

          <DialogDisclosure
            className={button({
              size: "small",
              maxInlineSize: "auto",
            })}
          >
            Choose weapons
          </DialogDisclosure>
        </DialogTrigger>
      )}

      {vehicle.can(EditMode.ADD, "mods") && (
        <DialogTrigger>
          <ChooseModsDialog />

          <DialogDisclosure
            className={button({
              theme: "beige",
              size: "small",
              maxInlineSize: "auto",
            })}
          >
            Choose mods
          </DialogDisclosure>
        </DialogTrigger>
      )}

      {vehicle.can(EditMode.ADD, "repairs") && (
        <DialogTrigger>
          <ChooseRepairsDialog />

          <DialogDisclosure
            className={button({
              theme: "beige",
              size: "small",
              maxInlineSize: "auto",
            })}
          >
            Record repairs
          </DialogDisclosure>
        </DialogTrigger>
      )}
    </div>
  );
}

function VehicleSpecialRules() {
  const vehicle = useVehicleContext();
  const { defaultMods, purchasedMods, repairs, rules } = vehicle;
  const specialCount =
    rules.length + defaultMods.length + purchasedMods.length + repairs.length;
  const showCosts = vehicle.gang.mode.itemCostVisibility === "VISIBLE";

  if (specialCount === 0) {
    return null;
  }

  return (
    <div>
      <Heading className={common.sectionHeading}>Special</Heading>

      <HeadingLevel>
        <div className={common.itemsList}>
          {rules.map((lookup, index) => {
            if (lookup.isFailure) {
              return (
                <ItemNotFound
                  key={index}
                  result={lookup}
                  item="a special rule"
                  onRemove={null}
                />
              );
            }

            return (
              <LoadoutItem
                key={index}
                item={lookup.value}
                type="Special Rule"
                onRemove={null}
                premium
              />
            );
          })}

          {defaultMods.map((lookup, index) => {
            if (lookup.isFailure) {
              return (
                <ItemNotFound
                  key={index}
                  result={lookup}
                  item="a default mod"
                  onRemove={null}
                />
              );
            }

            // Omitting cost as it's not relevant for default mods
            const { cost: _, ...defaultMod } = lookup.value;

            return (
              <LoadoutItem
                key={index}
                item={defaultMod}
                type="Default mod"
                onRemove={null}
                premium
              />
            );
          })}

          {purchasedMods.map((lookup, index) => {
            const removeMod = () => {
              const nextVehicle = vehicle.removeMod(index);
              void database.gangs.updateOne(nextVehicle.gang.data);
            };

            const handleRemove = vehicle.can(EditMode.REMOVE_FROM_LIST, "mods")
              ? removeMod
              : null;

            if (lookup.isFailure) {
              return (
                <ItemNotFound
                  key={index}
                  result={lookup}
                  item="a mod"
                  onRemove={handleRemove}
                />
              );
            }

            const mod = lookup.value;
            return (
              <LoadoutItem
                key={index}
                item={{
                  name: mod.name,
                  description: mod.description,
                  cost: showCosts ? mod.cost : null,
                }}
                type="Vehicle Mod"
                onRemove={handleRemove}
                premium
              />
            );
          })}

          {repairs.map((lookup, index) => {
            const removeRepair = () => {
              const nextVehicle = vehicle.removeRepair(index);
              void database.gangs.updateOne(nextVehicle.gang.data);
            };

            const handleRemove = vehicle.can(
              EditMode.REMOVE_FROM_LIST,
              "repairs",
            )
              ? removeRepair
              : null;

            if (lookup.isFailure) {
              return (
                <ItemNotFound
                  key={index}
                  result={lookup}
                  item="a repair"
                  onRemove={handleRemove}
                />
              );
            }

            const repair = lookup.value;
            return (
              <LoadoutItem
                key={index}
                item={{
                  name: repair.name,
                  description: repair.description,
                }}
                type={`${repair.location.name} Repair`}
                onRemove={handleRemove}
                extra={
                  repair.id === "mysterious_red_button" && (
                    <MysteriousRedButton />
                  )
                }
                premium
              />
            );
          })}
        </div>
      </HeadingLevel>
    </div>
  );
}

export function VehicleView() {
  const { readOnly, lookup } = useVehicle();

  if (lookup.isFailure) {
    return <FailureView failure={lookup.failure} />;
  }

  const vehicle = lookup.value;
  return (
    <ModelContext value={vehicle}>
      {vehicle.causeOfWriteOff ? (
        <WriteOffView reason={vehicle.causeOfWriteOff} />
      ) : (
        <div className={gang.container}>
          <VehicleProfileInfo />

          <HeadingLevel>
            <div className={common.sectionsStack}>
              <IssuesList />

              <VehicleWeaponsSection />

              <VehicleSpecialRules />
            </div>
          </HeadingLevel>

          {!readOnly && <VehicleActions />}
        </div>
      )}
    </ModelContext>
  );
}
